var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { ActiveUserInTrainingScenarioFacet, AttachToModelFacet, LinkToModelFacet, AskOkMessage, ControllerHand, MeshFacet, RemoveFacetFromModelMessage, RemoveFacetsOnDisconnectFacet, ResetSessionMessage, RoomChangedMessage, SessionResetPolicy, TomlFileFacet, TrainingModelStateFacet, TrainingVideoFacet, TrainingVideoPlaybackMessage, TrainingWearableFacet, UpdateFacetOnModelMessage, UpdateSessionResetPolicyMessage, TrainingVideoLoadingStateRequestMessage, TrainingVideoLoadingStateResponseMessage, TrainingFinishedMessage, TrainingStageChangedMessage, SetTrainingObjectStateMessage, worldToLocalTransform, convertToNumber16 } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { EnableWebXRMessage } from "@messages/xr";
import { ResetModelPickupsRequestMessage } from "@messages/attachment/AttachmentMessage";
import { DestinationReachedMessage, RequestPlayerWorldTransformMessage, ResponsePlayerWorldTransformMessage, SetPlayerWorldTransformMessage } from "@messages/locomotion/LocomotionMessages";
import { ControllerConnectedMessage, ControllerDisconnectedMessage, MouseConnectedMessage } from "@messages/controller/ControllerMessages";
import { DeactivateModuleMessage } from "@messages/module";
import { HistoryEventType, ResetModelScriptsRequestMessage } from "@messages/scripting/ScriptMessages";
import { modelPool, determineModelNameForPrinting, getModelNameByUID, computeLocalMatrixFromWorldMatrix } from "@modules/model";
import { trainingUserMistakeService } from "@modules/training/services/TrainingUserMistakeService";
import { ensureLocalPlayerControllersModel, ensureLocalPlayerHeadModel } from "@modules/player";
import { ModelEventHandler, NewModule, ModelEventType } from "@modules/module";
import { xrControllerService } from "@modules/xr/XRControllerService";
import { usersService } from "@modules/multiplayer/UsersService";
import { historyEventService } from "@modules/multiplayer";
import { camera } from "@modules/rendering/camera";
import { xrService } from "@modules/xr/XRService";
import { vrService } from "@modules/xr/VRService";
import { roomService } from "@modules/room/RoomService";
import { MessageService } from "../../ui/services/message.service";
import { trainingDebugService } from "../../ui/services/training-debug.service";
import { TrainingContext } from "./shared/scenarioTypes";
import { ButtonClickedEvent, StageChangedEvent, TrainingObjectStateChangedInternalEvent } from "./scenario/TrainingEvent";
import { TrainingMovementTrackerService } from "./services/TrainingMovementTrackerService";
import { TrainingModelStateService } from "./services/TrainingModelStateService";
import { TrainingSceneResetService } from "./services/TrainingSceneResetService";
import { TrainingUserEventService } from "./services/TrainingUserEventService";
import { TrainingVrHudService } from "./services/TrainingVrHudService";
import { TrackedController } from "./tracking/TrackedController";
import { ScenarioFactory } from "./services/ScenarioFactory";
import { TrainingObject } from "./scenario/TrainingObject";
import { TrackedCamera } from "./tracking/TrackedCamera";
import { TrainingWearableService, WearableItemStates } from "./services/TrainingWearableService";
const DIALOG_DISPLAY_TIME_MS = 10000;
const MOVEMENT_THRESHOLD_M_PER_SECONDS = 0.8; // Changed threshold for PTS demo
const MOVEMENT_THRESHOLD_CONTROLLER_M_PER_SECONDS = 0.8;
const TMP_ATTACHED_LOCAL_TRANSFORM = new Matrix4();
/**
 * Orchestrates interactive training scenarios so scenario definitions, wearable state, tracked user movement and training-specific UI all advance in a coordinated way.
 *
 * Facets:
 * - `TomlFileFacet` - Supplies the scenario definition that initializes the training orchestration services and active scenario.
 * - `TrainingModelStateFacet`, `TrainingWearableFacet`, `TrainingVideoFacet` and `MeshFacet` - Provide the per-model training state, wearable metadata, video state and geometry information that the scenario logic consumes.
 *
 * Messages:
 * - `ControllerConnectedMessage`, `MouseConnectedMessage` and `ControllerDisconnectedMessage` - Track connected controllers and mice as training objects (e.g. hands) and remove them again on disconnect.
 * - `RoomChangedMessage` - Rebuilds the active scenario once services are initialized, to reflect the new room context.
 * - `SetTrainingObjectStateMessage` - Directly sets a tracked training object to a given state from outside the scenario's own event flow.
 *
 * Events:
 * - `ModelEventType.TRANSFORM_UPDATED` - Tracks movement and attachment changes of training-relevant models and users.
 */
export class TrainingOrchestratorModule extends NewModule {
    // Safe accessors for services - only callable after TOML initialization
    get scenarioFactory() {
        if (this._scenarioFactory === undefined)
            throw new Error("[training] ScenarioFactory not initialized - no TOML file received");
        return this._scenarioFactory;
    }
    get trainingUserEventService() {
        if (this._trainingUserEventService === undefined)
            throw new Error("[training] TrainingUserEventService not initialized");
        return this._trainingUserEventService;
    }
    get trainingModelStateService() {
        if (this._trainingModelStateService === undefined)
            throw new Error("[training] TrainingModelStateService not initialized");
        return this._trainingModelStateService;
    }
    get trainingMovementTrackerService() {
        if (this._trainingMovementTrackerService === undefined)
            throw new Error("[training] TrainingMovementTrackerService not initialized");
        return this._trainingMovementTrackerService;
    }
    get trainingVrHudService() {
        if (this._trainingVrHudService === undefined)
            throw new Error("[training] TrainingVrHudService not initialized");
        return this._trainingVrHudService;
    }
    get trainingWearableService() {
        if (this._trainingWearableService === undefined)
            throw new Error("[training] TrainingWearableService not initialized");
        return this._trainingWearableService;
    }
    /**
     * Sets up subscriptions for controller/mouse connect and disconnect, training video facets and
     * `SetTrainingObjectStateMessage`, and registers a debug-only keydown listener (space bar) to advance the stage.
     */
    constructor() {
        super("TrainingOrchestratorModule");
        this.skipStageChangesWhileApplyingPreconditionState = false;
        this.inputFieldValues = new Map();
        this.trainingSceneResetService = new TrainingSceneResetService();
        // Services - only initialized when a valid TOML file is present
        this.servicesInitialized = false;
        this.showUserHUD = false;
        this.scenarioSettings = [];
        this.hasTrainingVideoFacet = false;
        // Buffers for facet data received before TOML
        this.bufferedModelStateFacets = [];
        this.bufferedWearableFacets = [];
        this.bufferedMeshUids = [];
        this.bufferedModelTransforms = [];
        this.bufferedVideoFacets = [];
        /// Handlers ///
        this.handleTrainingVideoFacet = (modelUid, facet) => {
            this.hasTrainingVideoFacet = true;
            if (!this.servicesInitialized) {
                this.bufferedVideoFacets.push({ uid: modelUid, facet });
                return;
            }
            this.handleTrainingVideoFacetInternal(modelUid, facet);
        };
        this.handleControllerConnectedMessage = (message) => {
            const controllerHand = ControllerConnectedMessage.is(message) ? message.hand : ControllerHand.RightHand;
            let controllerHandName = "UNKNOWN";
            let objectID = "UNKNOWN";
            switch (controllerHand) {
                case ControllerHand.LeftHand:
                    controllerHandName = getModelNameByUID(message.uid);
                    objectID = "LEFT_HAND";
                    break;
                case ControllerHand.RightHand:
                    controllerHandName = getModelNameByUID(message.uid);
                    objectID = "RIGHT_HAND";
                    break;
                default:
                    console.warn(`[training] Unsupported controller hand "${controllerHand}"`);
            }
            const controllerModel = MouseConnectedMessage.is(message)
                ? modelPool.getModel(message.uid)
                : modelPool.findModelInHierarchy(controllerHandName, ensureLocalPlayerControllersModel());
            if (controllerModel !== undefined) {
                const trainingObject = new TrainingObject(objectID, controllerModel.getUID(), controllerHandName, [], []);
                this.addTrainingObject(trainingObject);
            }
        };
        this.handleControllerDisconnectedMessage = (message) => {
            const trainingObject = this.findTrainingObjectFromContextByUID(message.uid);
            if (trainingObject === undefined)
                return;
            this.removeTrainingObject(trainingObject);
        };
        this.handleOnScenarioBuilt = (scenario, trainingContext, trainingSettings) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.isActive) {
                console.warn("[training] Scenario already active");
                return;
            }
            this.scenarioSettings.push(...trainingSettings);
            console.debug("[training] Created scenario", scenario, trainingContext);
            this.scenario = scenario;
            this.initialStageId = (_a = scenario.getCurrentState()) === null || _a === void 0 ? void 0 : _a.id;
            this.initialPlayerTransform = undefined;
            trainingDebugService.setScenario(scenario);
            // Initialize context
            this.context = trainingContext;
            for (const sceneObject of this.scenario.objects)
                this.context.trainingObjectMap.set(sceneObject.id, sceneObject);
            // Add head collision model as training object
            const headModel = ensureLocalPlayerHeadModel();
            if (headModel !== undefined) {
                const headTrainingObject = new TrainingObject("HEAD", headModel.getUID(), headModel.getName(), [], []);
                this.addTrainingObject(headTrainingObject, true);
            }
            // Add all pending training objects that were created separately from the TOML file (e.g controllers)
            this.pendingTrainingObjects.forEach((trainingObject) => {
                this.addTrainingObject(trainingObject, true);
            });
            this.pendingTrainingObjects.clear();
            // Initialize training event service
            this.trainingUserEventService.updateContext(this.context);
            for (const scenarioEvent of this.scenario.events) {
                this.trainingUserEventService.registerEvent(scenarioEvent);
            }
            this.trainingUserEventService.init();
            // Initialize training model service
            for (const scenarioObject of this.scenario.objects) {
                this.trainingModelStateService.addScenarioObject(scenarioObject);
            }
            this.trainingSceneResetService.capture(this.scenarioFactory.getScenarioModelUids());
            // Process general scenario settings
            this.processScenarioSettingsBySettingsId("general");
            // wait here until all videos are loaded to avoid starting the training before
            // the videos are ready which can lead to issues with the synchronization of the video state and the training state
            if (this.hasTrainingVideoFacet) {
                const allVideosLoaded = yield this.waitForAllVideosToBeLoaded();
                if (!allVideosLoaded)
                    return;
            }
            void MessageService.showStartTraining(scenario.name, this.scenarioFactory.getInputConfig(), (fieldValues) => { void this.activateScenario(fieldValues); }, () => { this.enableSpectatorMode(); });
        });
        this.handleOnEventTriggered = (event) => __awaiter(this, void 0, void 0, function* () {
            console.log(`[training] Scenario event triggered type="${String(event.type)}" id="${event.id}"`);
            if (event.isInternalEvent && TrainingObjectStateChangedInternalEvent.is(event)) {
                trainingDebugService.logEvent("OBJECT_STATE_CHANGED [internal]", event.scenarioObject.id, `→ ${event.nextState.name}`);
            }
            else {
                trainingDebugService.logEvent(String(event.type), event.id);
            }
            if (!this.isActive)
                return;
            switch (event.type) {
                case "CONTROLLER_ACTION_CLICK":
                    yield this.updateStage(event);
                    if (ButtonClickedEvent.is(event))
                        yield this.updateTrainingObjects(event);
                    break;
                case "OBJECT_STATE_CHANGED":
                    if (!event.isInternalEvent) {
                        yield this.updateStage(event);
                        yield this.updateTrainingObjects(event);
                    }
                    else {
                        // Only model and stage listen to this event
                        this.trainingModelStateService.onTrainingObjectStateChanged(event);
                        this.trainingUserEventService.updateIcons(event.context);
                        this.trainingWearableService.onTrainingObjectStateChanged(event);
                        const trainingObjectStateChangedEvent = event;
                        this.invokePredefinedTrainingObjectCallbacks(trainingObjectStateChangedEvent.scenarioObject.id, trainingObjectStateChangedEvent.nextState.name);
                        this.trainingUserEventService.fireObjectStateChangedEvents(trainingObjectStateChangedEvent.scenarioObject.id);
                    }
                    break;
                case "ATTACHMENT_CHANGED":
                case "OBJECT_COLLISION":
                case "TIMER_EXPIRED":
                case "VIDEO_STATE_CHANGED":
                case "WEARABLE_STATE_CHANGED":
                case "WEARABLE_STATE_CHANGE_FAILED":
                    // Update the stage before the object to avoid side effects, because the training object does not depend on the
                    // stage but the stage might depend on the training object state. In any case the stage will still be correctly
                    // updated on OBJECT_STATE_CHANGED since it also listens to this event
                    yield this.updateStage(event);
                    yield this.updateTrainingObjects(event);
                    break;
                case "STAGE_CHANGED":
                    yield this.updateTrainingObjects(event);
                    break;
                default:
                    console.warn(`[training] Unsupported event type "${String(event.type)}"`);
            }
            if (this.scenario !== undefined) {
                trainingDebugService.notifyChanged();
            }
        });
        this.handleTrackedControllerConnected = (c) => {
            const name = TrainingOrchestratorModule.translateControllerName(c.hand);
            const existing = this.trainingMovementTrackerService.getTrackedObjects().get(name);
            // Keep the already collected statistics when a controller reconnects (e.g. after leaving and re-entering VR)
            if (existing instanceof TrackedController) {
                existing.reconnect(c);
                return;
            }
            this.trainingMovementTrackerService.addObject(name, new TrackedController(name, c, c.uid), true, MOVEMENT_THRESHOLD_CONTROLLER_M_PER_SECONDS);
        };
        this.handleTrackedControllerDisconnected = (c) => {
            // Don't remove the tracked object, otherwise its statistics would be missing in the training results
            // (controllers get disconnected e.g. when the XR session ends right before the end dialog is shown)
            this.trainingMovementTrackerService.getTrackedObjects().forEach((obj) => {
                if (obj instanceof TrackedController && obj.controllerUid === c.uid)
                    obj.disconnect();
            });
        };
        this.handleSetTrainingObjectStateMessage = (msg) => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!this.isActive || msg.modelUid === undefined)
                return;
            const trainingObject = this.findTrainingObjectFromContextByUID(msg.modelUid);
            if (trainingObject === undefined) {
                console.warn(`[training] SetTrainingObjectStateMessage: no training object found for model UID ${msg.modelUid}`);
                return;
            }
            const nextState = (_a = trainingObject.states) === null || _a === void 0 ? void 0 : _a.find(s => s.name === msg.stateName);
            if (nextState === undefined) {
                console.warn(`[training] SetTrainingObjectStateMessage: training object "${trainingObject.id}" has no state "${msg.stateName}"`);
                return;
            }
            const stateNode = trainingObject.getStateByNameOrId(msg.stateName);
            if (stateNode !== undefined)
                trainingObject.setCurrentState(stateNode);
            yield this.handleOnEventTriggered(new TrainingObjectStateChangedInternalEvent(trainingObject, nextState, this.context));
        });
        this.isActive = false;
        this.context = new TrainingContext();
        this.pendingTrainingObjects = new Set();
        this.connection.subscribeByType(ControllerConnectedMessage, this.handleControllerConnectedMessage);
        this.connection.subscribeByType(ControllerDisconnectedMessage, this.handleControllerDisconnectedMessage);
        this.connection.subscribeByType(MouseConnectedMessage, this.handleControllerConnectedMessage);
        this.connection.subscribeByFacetType(TrainingVideoFacet, this.handleTrainingVideoFacet);
        this.connection.subscribeByType(SetTrainingObjectStateMessage, this.handleSetTrainingObjectStateMessage);
        // --- only for debugging purposes ---
        // Press space to change to the next stage
        const handleKeyDown = (event) => {
            if (event.code === "Space") {
                void this.updateStage(undefined);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        // -----------------------------------
    }
    /// Getters ///
    getScenario() {
        return this.scenario;
    }
    /**
     * Returns the name of the current state of the training object identified by the given UID or training object ID,
     * or `undefined` if no matching training object or state exists.
     */
    getTrainingObjectState(uidOrTrainingObjectId) {
        var _a, _b;
        return (_b = (_a = this.findTrainingObjectByUidOrId(uidOrTrainingObjectId)) === null || _a === void 0 ? void 0 : _a.getCurrentState()) === null || _b === void 0 ? void 0 : _b.name;
    }
    addStageError(stageId, text, errorType) {
        var _a;
        const stage = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.stages.find(candidate => candidate.id === stageId);
        if (stage === undefined) {
            console.warn(`[training] addStageError: no stage found with id "${stageId}"`);
            return;
        }
        trainingUserMistakeService.addStageError(stage, text, errorType);
    }
    /**
     * Looks up the training object identified by the given UID or training object ID and, if it has a matching state,
     * sets it as its current state and fires the resulting `TrainingObjectStateChangedInternalEvent`.
     */
    setTrainingObjectState(uidOrTrainingObjectId, stateName) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const trainingObject = this.findTrainingObjectByUidOrId(uidOrTrainingObjectId);
            if (trainingObject === undefined) {
                console.warn(`[training] setTrainingObjectState: no training object found for "${uidOrTrainingObjectId}"`);
                return;
            }
            const nextState = (_a = trainingObject.states) === null || _a === void 0 ? void 0 : _a.find((s) => s.name === stateName);
            if (nextState === undefined) {
                console.warn(`[training] setTrainingObjectState: training object "${trainingObject.id}" has no state "${stateName}"`);
                return;
            }
            const stateNode = trainingObject.getStateByNameOrId(stateName);
            if (stateNode !== undefined)
                trainingObject.setCurrentState(stateNode);
            yield this.handleOnEventTriggered(new TrainingObjectStateChangedInternalEvent(trainingObject, nextState, this.context));
        });
    }
    findTrainingObjectByUidOrId(uidOrTrainingObjectId) {
        var _a;
        if (typeof uidOrTrainingObjectId === "number") {
            return this.findTrainingObjectFromContextByUID(uidOrTrainingObjectId);
        }
        return (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.objects.find((obj) => obj.id === uidOrTrainingObjectId);
    }
    /// Life-cycle methods ///
    /**
     * Subscribes to the facets (`TomlFileFacet`, `TrainingModelStateFacet`, `TrainingWearableFacet`, `MeshFacet`) and
     * `RoomChangedMessage` that drive scenario construction, buffering data until the training services are
     * initialized. Also toggles the VR HUD's visibility when the XR presentation state changes.
     */
    onInit() {
        // Connections necessary to build the scenario
        this.connection.subscribeByFacetType(TomlFileFacet, (uid, facet) => {
            this.tomlFileFacetModelUid = uid;
            trainingDebugService.setTomlDetected(true);
            this.initializeServices();
            this.scenarioFactory.setToml(facet.tomlFileContents);
        });
        this.connection.subscribeByFacetType(TrainingModelStateFacet, (uid, facet) => {
            const name = determineModelNameForPrinting(uid);
            console.debug("[training] TrainingModelStateFacet found for model", name, uid);
            if (!this.servicesInitialized) {
                this.bufferedModelStateFacets.push({ uid, facet });
                return;
            }
            this.scenarioFactory.addModelStateFacet(uid, facet);
        });
        this.connection.subscribeByFacetType(TrainingWearableFacet, (uid, facet) => {
            var _a;
            const modelName = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getName();
            if (modelName === undefined)
                return;
            console.debug("[training] TrainingWearableFacet found for model", modelName, uid);
            if (!this.servicesInitialized) {
                this.bufferedWearableFacets.push({ uid, facet });
                return;
            }
            this.processWearableFacet(uid, facet, modelName);
        });
        this.connection.subscribeByFacetType(MeshFacet, (uid, _facet) => {
            var _a;
            const name = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getName();
            console.debug("[training] MeshFacet found for model", name, uid);
            if (!this.servicesInitialized) {
                this.bufferedMeshUids.push(uid);
                return;
            }
            this.scenarioFactory.addModel(uid);
        });
        this.connection.subscribeByType(RoomChangedMessage, (_message) => {
            if (!this.servicesInitialized)
                return;
            this.scenarioFactory.build();
        });
        // Listen to XR presentation changes to show/hide the VR HUD
        xrService.onPresentingChangedDelegate.add((isPresenting) => {
            var _a;
            if (this._trainingVrHudService === undefined)
                return;
            (_a = this.trainingVrHudService) === null || _a === void 0 ? void 0 : _a.setVisibility(this.showUserHUD && isPresenting);
        });
    }
    /// ? ///
    initializeServices() {
        var _a;
        if (this.servicesInitialized)
            return;
        this.servicesInitialized = true;
        this._scenarioFactory = new ScenarioFactory();
        this._trainingUserEventService = new TrainingUserEventService();
        this._trainingModelStateService = new TrainingModelStateService();
        this._trainingMovementTrackerService = new TrainingMovementTrackerService({ defaultThreshold: MOVEMENT_THRESHOLD_M_PER_SECONDS });
        this._trainingVrHudService = new TrainingVrHudService(camera.vrHud);
        this._trainingWearableService = new TrainingWearableService(this.connection, this._trainingVrHudService);
        this._scenarioFactory.onScenarioBuiltDelegate.add(this.handleOnScenarioBuilt);
        this._trainingUserEventService.onEventTriggeredDelegate.add(this.handleOnEventTriggered);
        // Flush buffered data
        for (const { uid, facet } of this.bufferedModelStateFacets) {
            this._scenarioFactory.addModelStateFacet(uid, facet);
        }
        this.bufferedModelStateFacets.length = 0;
        for (const { uid, facet } of this.bufferedWearableFacets) {
            const modelName = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getName();
            if (modelName !== undefined)
                this.processWearableFacet(uid, facet, modelName);
        }
        this.bufferedWearableFacets.length = 0;
        for (const uid of this.bufferedMeshUids) {
            this._scenarioFactory.addModel(uid);
        }
        this.bufferedMeshUids.length = 0;
        for (const { uid, position } of this.bufferedModelTransforms) {
            this._scenarioFactory.addModelTransformation(uid, position);
        }
        this.bufferedModelTransforms.length = 0;
        for (const { uid, facet } of this.bufferedVideoFacets) {
            this.handleTrainingVideoFacetInternal(uid, facet);
        }
        this.bufferedVideoFacets.length = 0;
    }
    processWearableFacet(uid, facet, modelName) {
        this.scenarioFactory.addWearableFacet(uid, facet);
        const itemState = "unworn";
        const item = {
            uid,
            name: modelName,
            state: itemState,
            wearingPosition: facet.wearingPosition,
            videoId: facet.videoId
        };
        this.trainingWearableService.addClothingItem(uid, item);
        this.scenarioFactory.addPredefinedScenarioObject("WEARABLE-" + modelName, WearableItemStates, uid);
    }
    processScenarioSettingsBySettingsId(id) {
        for (const setting of this.scenarioSettings) {
            if (setting.id === id) {
                switch (setting.name) {
                    case "userMovementSpeedAudioVisualFeedback":
                        this.trainingMovementTrackerService.enableAudioVisualFeedback(typeof setting.value === "boolean" ? setting.value : false);
                        break;
                    case "userMovementSpeedAudioVisualFeedbackThreshold":
                        this.trainingMovementTrackerService.getTrackedObjects().forEach((obj) => {
                            this.trainingMovementTrackerService.setThreshold(obj.name, typeof setting.value === "number" ? setting.value : MOVEMENT_THRESHOLD_M_PER_SECONDS);
                        });
                        break;
                    case "userMovementSpeedTrackingActive":
                        this.trainingMovementTrackerService.setTrackingEnabled(typeof setting.value === "boolean" ? setting.value : false);
                        break;
                    case "showUserHUD":
                        this.showUserHUD = typeof setting.value === "boolean" ? setting.value : false;
                        break;
                    default:
                }
            }
        }
    }
    handleTrainingVideoFacetInternal(modelUid, facet) {
        this.hasTrainingVideoFacet = true;
        const callback = (newState) => {
            console.log("[training] Video state changed for video", facet.id, "to", newState);
            switch (newState) {
                case "play":
                    this.connection.sendMultiple([
                        new TrainingVideoPlaybackMessage(facet.id, "show"),
                        new TrainingVideoPlaybackMessage(facet.id, "play")
                    ]);
                    break;
                case "pause":
                    this.connection.send(new TrainingVideoPlaybackMessage(facet.id, "pause"));
                    break;
                case "stop":
                case "ended":
                    this.connection.sendMultiple([new TrainingVideoPlaybackMessage(facet.id, "stop"), new TrainingVideoPlaybackMessage(facet.id, "hide")]);
                    break;
                default:
                    console.warn(`[training] Unsupported video state "${newState}" for video "${facet.id}"`);
            }
        };
        this.scenarioFactory.addPredefinedScenarioObject("VIDEO-" + facet.id, ["stop", "play", "pause", "ended"], modelUid, callback);
    }
    waitForAllVideosToBeLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield new Promise((resolve, reject) => {
                let numberOfChecks = 0;
                // Create loading overlay
                const overlay = document.createElement("div");
                overlay.id = "video-loading-overlay";
                overlay.style.cssText = "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.7);display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:10000;color:#fff;font-family:'Titillium Web',sans-serif;";
                const spinner = document.createElement("div");
                spinner.style.cssText = "width:48px;height:48px;border:4px solid rgba(255,255,255,0.3);border-top:4px solid #fff;border-radius:50%;animation:video-loading-spin 1s linear infinite;";
                const style = document.createElement("style");
                style.textContent = "@keyframes video-loading-spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}";
                overlay.appendChild(style);
                overlay.appendChild(spinner);
                const label = document.createElement("div");
                label.style.cssText = "margin-top:16px;font-size:18px;text-align:center;";
                label.textContent = "Loading training videos...";
                overlay.appendChild(label);
                document.body.appendChild(overlay);
                const removeOverlay = () => {
                    overlay.remove();
                };
                const interval = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const response = yield this.connection.askByMessageType(new TrainingVideoLoadingStateRequestMessage(), TrainingVideoLoadingStateResponseMessage);
                        const loaded = response.numberOfVideosLoaded;
                        const total = response.numberOfVideosToLoad;
                        const failed = response.numberOfFailedVideos;
                        const failedText = failed > 0 ? `${failed} failed to load` : ``;
                        label.innerHTML = `Loading training videos... (${loaded}/${total})<br>${failedText}`;
                        if (total === loaded + failed) {
                            if (failed > 0)
                                console.warn(`${failed} videos failed to load`);
                            clearInterval(interval);
                            if (failed === 0) {
                                removeOverlay();
                                resolve(true);
                            }
                            else {
                                // Update label text and add a round close button centered below it
                                const closeButton = document.createElement("button");
                                closeButton.textContent = "✕";
                                closeButton.style.cssText = [
                                    "display:flex",
                                    "align-items:center",
                                    "justify-content:center",
                                    "width:48px",
                                    "min-width:48px",
                                    "height:48px",
                                    "min-height:48px",
                                    "flex-shrink:0",
                                    "box-sizing:border-box",
                                    "margin-top:20px",
                                    "background:rgba(255,255,255,0.12)",
                                    "border:1.5px solid rgba(255,255,255,0.5)",
                                    "color:#fff",
                                    "font-size:20px",
                                    "line-height:1",
                                    "font-family:'Titillium Web',sans-serif",
                                    "border-radius:50%",
                                    "cursor:pointer",
                                    "transition:background 0.2s,border-color 0.2s,transform 0.15s",
                                    "padding:0"
                                ].join(";");
                                closeButton.addEventListener("mouseenter", () => {
                                    closeButton.style.background = "rgba(255,255,255,0.28)";
                                    closeButton.style.borderColor = "#fff";
                                    closeButton.style.transform = "scale(1.1)";
                                });
                                closeButton.addEventListener("mouseleave", () => {
                                    closeButton.style.background = "rgba(255,255,255,0.12)";
                                    closeButton.style.borderColor = "rgba(255,255,255,0.5)";
                                    closeButton.style.transform = "scale(1)";
                                });
                                closeButton.addEventListener("click", () => {
                                    removeOverlay();
                                    resolve(false);
                                });
                                overlay.appendChild(closeButton);
                            }
                        }
                        else {
                            numberOfChecks++;
                            if (numberOfChecks > 1000) {
                                // after 1000 seconds of waiting, we assume that there is an issue with loading the videos and we start the training anyway to avoid blocking the user indefinitely
                                clearInterval(interval);
                                removeOverlay();
                                reject(new Error(`[training] Tried 100 times to wait for all training videos to be loaded, but they are still not loaded. Starting the training anyway to avoid blocking the user indefinitely.`));
                            }
                        }
                    }
                    catch (error) {
                        clearInterval(interval);
                        removeOverlay();
                        reject(error);
                    }
                }), 100);
            });
        });
    }
    waitForVRSession() {
        return __awaiter(this, void 0, void 0, function* () {
            if (xrService.isPresenting())
                return;
            yield new Promise((resolve) => {
                const onPresentingChanged = (isPresenting) => {
                    if (!isPresenting)
                        return;
                    xrService.onPresentingChangedDelegate.remove(onPresentingChanged);
                    resolve();
                };
                xrService.onPresentingChangedDelegate.add(onPresentingChanged);
                if (xrService.isPresenting()) {
                    xrService.onPresentingChangedDelegate.remove(onPresentingChanged);
                    resolve();
                }
            });
        });
    }
    handleModelTransformEvent(model, _localTransform) {
        const worldPosition = model.getWorldPosition().clone();
        if (!this.servicesInitialized) {
            this.bufferedModelTransforms.push({ uid: model.getUID(), position: worldPosition });
            return;
        }
        this.scenarioFactory.addModelTransformation(model.getUID(), worldPosition);
        if (this.scenario !== undefined) {
            trainingDebugService.notifyChanged();
        }
    }
    /// Scenario helper methods ///
    activateScenario(inputFieldValues) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            // First check if the scenario is valid
            if (!this.scenarioFactory.validateTrainingObjects())
                return;
            // If we find a ActiveUserInTrainingScenarioFacet that could mean that someone else is already doing the training
            const modelsWithActiveUserTrainingFacet = modelPool.findModelsWithFacets(ActiveUserInTrainingScenarioFacet);
            const activeUserInfo = modelsWithActiveUserTrainingFacet.length === 1
                ? modelsWithActiveUserTrainingFacet[0].getFacet(ActiveUserInTrainingScenarioFacet)
                : modelsWithActiveUserTrainingFacet.length > 1
                    ? (() => {
                        const modelNames = modelsWithActiveUserTrainingFacet.map((m) => m.getName()).join(",");
                        console.error(`[model] More than one ActiveUserInTrainingScenarioFacet found in ${modelNames}`);
                        return undefined;
                    })()
                    : undefined;
            // Check if another user is already in a training scenario
            if (activeUserInfo !== undefined &&
                usersService.getNumberOfUsers() > 1 && // More than one user (the local) is connected
                usersService.hasUserByClientId(usersService.getLocalUser().clientId) && // The local user is connected
                activeUserInfo.activeUserInTrainingScenarioFacetData.trainingScenarioName === ((_a = this.scenario) === null || _a === void 0 ? void 0 : _a.name) && // Check if the training scenario is the same as the one we want to start
                activeUserInfo.activeUserInTrainingScenarioFacetData.activeClientIdx !== undefined) {
                // Check if the activeClientIdx is defined and no other user is logged into the scenario
                const message = `Another user (${activeUserInfo.activeUserInTrainingScenarioFacetData.activeUserName}) is already in a training scenario. Only one user can be in a training scenario at a time.`;
                console.warn(`[training] ${message}`);
                MessageService.showMessage("info", message);
                this.enableSpectatorMode(); // enable spectator mode instead
                // ToDo: Ask if the user wants an own session and if yes create a new session with this layout and redirect to it
            }
            else {
                const messages = [];
                // No other user is in a training scenario, we can activate the training for the local user and overwrite the ActiveUserInTrainingScenarioFacet
                if (this.tomlFileFacetModelUid === undefined) {
                    console.error(`[training] tomlFileFacetModelUid is undefined when activation training scenario, that should not happen`);
                    return;
                }
                // Reset session when all clients disconnected
                this.informServerToResetSessionWhenAllClientsDisconnected();
                // Enable VR if supported
                if (vrService.isVRSupported) {
                    try {
                        yield this.connection.askByMessageType(new EnableWebXRMessage(true), AskOkMessage);
                    }
                    catch (e) {
                        console.warn("[training]", e);
                    }
                    yield this.waitForVRSession();
                }
                // Create and send ActiveUserInTrainingScenarioFacet
                const activeUserInTrainingScenarioFacetData = {
                    activeClientIdx: usersService.getLocalUser().clientIdx,
                    activeUserName: (_b = usersService.getLocalUser().customUsername) !== null && _b !== void 0 ? _b : "Unknown",
                    trainingScenarioName: (_d = (_c = this.scenario) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : "Unknown",
                    startTrainingTime: new Date().toISOString()
                };
                const activeUserInTrainingScenarioFacet = new ActiveUserInTrainingScenarioFacet(activeUserInTrainingScenarioFacetData);
                // lock the training for other users by setting the ActiveUserInTrainingScenarioFacet
                messages.push(new UpdateFacetOnModelMessage(this.tomlFileFacetModelUid, activeUserInTrainingScenarioFacet));
                messages.push(new UpdateFacetOnModelMessage(this.tomlFileFacetModelUid, new RemoveFacetsOnDisconnectFacet([activeUserInTrainingScenarioFacet], usersService.getLocalUser().clientIdx)));
                this.connection.sendMultiple(messages);
                // activate the training for the local user
                const playerTransform = yield this.connection.askByMessageType(new RequestPlayerWorldTransformMessage(), ResponsePlayerWorldTransformMessage);
                this.initialPlayerTransform = playerTransform.trans;
                this.isActive = true;
                this.trainingUserEventService.setActive(this.isActive);
                // Apply dialog-selected initial states for input fields
                if (inputFieldValues !== undefined) {
                    this.inputFieldValues = new Map(inputFieldValues);
                    for (const [objectId, stateId] of inputFieldValues) {
                        yield this.setInputObjectState(objectId, stateId);
                    }
                }
                this.trainingMovementTrackerService.setActiveStage((_f = (_e = this.scenario) === null || _e === void 0 ? void 0 : _e.getCurrentState()) === null || _f === void 0 ? void 0 : _f.id);
                this.registerMovementTrackerObjects();
                this.processScenarioSettingsBySettingsId("general");
                // inform the user about the beginning of the training
                const scenarioName = (_h = (_g = this.scenario) === null || _g === void 0 ? void 0 : _g.name) !== null && _h !== void 0 ? _h : "Unknown Scenario";
                const userName = activeUserInTrainingScenarioFacetData.activeUserName;
                MessageService.showMessage("info", `Trainingseinheit "${scenarioName}" für "${userName}" gestartet.`, DIALOG_DISPLAY_TIME_MS);
                // Set the start stage
                const startStage = (_l = (_k = (_j = this.scenario) === null || _j === void 0 ? void 0 : _j.getCurrentState()) === null || _k === void 0 ? void 0 : _k.id) !== null && _l !== void 0 ? _l : "";
                yield this.forwardStateChangeEvent(startStage, startStage);
                this.processScenarioSettingsBySettingsId(startStage);
                this.displayCurrentObjective();
            }
        });
    }
    restartScenario() {
        this.finishScenario();
        this.resetSessionOnServer();
        window.location.reload();
    }
    finishScenario() {
        if (this.tomlFileFacetModelUid === undefined)
            return;
        this.connection.send(new RemoveFacetFromModelMessage(this.tomlFileFacetModelUid, ActiveUserInTrainingScenarioFacet));
        this.connection.send(new TrainingFinishedMessage(false));
    }
    revisitStagesWithMadeMistakes() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.scenario === undefined)
                return;
            trainingUserMistakeService.resetMistakes();
            if (trainingUserMistakeService.getNumberOfSequencesWithMistakes() === 0) {
                this.finishScenario();
                return;
            }
            this.connection.send(new TrainingFinishedMessage(true));
            this.context.trainingEventMetaInformation.clear();
            trainingUserMistakeService.isRevisitErrorMode = true;
            trainingUserMistakeService.updateStageWithUserMistake(undefined);
            const currentSequence = trainingUserMistakeService.getCurrentTrainingSequence();
            if (currentSequence === undefined)
                return;
            if (!xrService.isPresenting() && vrService.isVRSupported) {
                try {
                    yield this.connection.askByMessageType(new EnableWebXRMessage(true), AskOkMessage);
                }
                catch (e) {
                    console.warn("[training]", e);
                }
                yield this.waitForVRSession();
            }
            void this.activateSequence(currentSequence);
        });
    }
    activateSequence(currentSequence) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            // If we are in mistake revisit mode and this is the initial call (event is undefined),
            // we want to jump to the first stage with a mistake
            if (currentSequence.stages.length === 0 || this.scenario === undefined)
                return;
            const nextStageIdWithMistake = currentSequence.stages[0];
            if (nextStageIdWithMistake !== undefined) {
                const nextStage = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.getStateByNameOrId(undefined, nextStageIdWithMistake.id);
                if (nextStage === undefined)
                    return;
                (_b = this.scenario) === null || _b === void 0 ? void 0 : _b.setCurrentState(nextStage);
                // Before we switch to the next stage with mistake, we need to process the precondition state of the stage
                // so that the user can directly start from
                // the precondition state of the stage without having to trigger the events again that lead to the mistake
                yield this.processPreconditionStateForSequence(currentSequence);
                // Now we can switch to the next stage with mistake
                const nextStageId = nextStage.data.id;
                // Update the movement tracker to track the user movement in the revisit stages as well
                this.trainingMovementTrackerService.setActiveStage(nextStageId);
                // Process settings for the new stage
                this.processScenarioSettingsBySettingsId(nextStageId);
                // set the current stage in the context which is used for the evaluation of the events and conditions in the stage
                this.context.stage = nextStageId;
                trainingDebugService.setCurrentStageId(nextStageId);
                // Also forward this event to the training user service
                this.trainingUserEventService.updateStage();
                // Finally, display the current objective in the VR HUD
                // so that the user directly sees what the objective of the stage is when we jump to it
                this.displayCurrentObjective();
            }
        });
    }
    processPreconditionStateForSequence(sequence) {
        return __awaiter(this, void 0, void 0, function* () {
            this.skipStageChangesWhileApplyingPreconditionState = true;
            try {
                yield this.applyPreconditionStateForSequence(sequence);
            }
            finally {
                this.skipStageChangesWhileApplyingPreconditionState = false;
            }
        });
    }
    applyPreconditionStateForSequence(sequence) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            // Set the current stage in the context
            this.context.stage = sequence.stages[0].id;
            trainingDebugService.setCurrentStageId(this.context.stage);
            // Set the scenario's StateMachine to the correct stage node
            if (this.scenario !== undefined) {
                const stageNode = this.scenario.getStateByNameOrId(undefined, this.context.stage);
                if (stageNode !== undefined) {
                    this.scenario.setCurrentState(stageNode);
                }
            }
            // Clear collision and attachment meta information for a clean state
            this.context.trainingEventMetaInformation.delete("OBJECT_COLLISION_LIST");
            const trainingObjectModelUids = this.trainingSceneResetService.getModelUids();
            if (trainingObjectModelUids.length > 0) {
                yield this.connection.askByMessageType(new ResetModelPickupsRequestMessage(trainingObjectModelUids), AskOkMessage);
                this.trainingSceneResetService.restore();
                modelPool.flushMessages();
            }
            if (sequence.stages[0].id === this.initialStageId) {
                if (this.initialPlayerTransform === undefined)
                    throw new Error("[training] Missing initial player transform for revisit");
                yield this.connection.askByMessageType(new SetPlayerWorldTransformMessage(this.initialPlayerTransform), DestinationReachedMessage);
            }
            // Reset all training objects to their initial state
            this.context.trainingObjectMap.forEach((trainingObject) => {
                const allStates = trainingObject.getAllStates();
                if (allStates.length > 0) {
                    trainingObject.setCurrentState(allStates[0]);
                    this.trainingModelStateService.onTrainingObjectStateChanged(new TrainingObjectStateChangedInternalEvent(trainingObject, allStates[0].data, this.context));
                    if (trainingObject.id.startsWith("VIDEO-")) {
                        this.invokePredefinedTrainingObjectCallbacks(trainingObject.id, "stop");
                    }
                }
            });
            // Reset the scripts attached to the training objects (and their children) to their initial state as well
            if (trainingObjectModelUids.length > 0) {
                yield this.connection.askByMessageType(new ResetModelScriptsRequestMessage(trainingObjectModelUids, false), AskOkMessage);
            }
            // Re-apply user input field values so redo doesn't lose user-selected state
            for (const [objectId, stateId] of this.inputFieldValues) {
                yield this.setInputObjectState(objectId, stateId);
            }
            const explicitlyPositionedObjects = new Set();
            const objectConditionsList = sequence.precondition;
            if (objectConditionsList !== undefined) {
                for (const objectCondition of objectConditionsList) {
                    const trainingObject = this.context.trainingObjectMap.get(objectCondition.objectName);
                    // reset the training object to its initial state
                    if ("stateName" in objectCondition) {
                        if (trainingObject !== undefined) {
                            const state = trainingObject.getStateByNameOrId(objectCondition.stateName, undefined);
                            if (state !== undefined) {
                                trainingObject.setCurrentState(state);
                                this.trainingModelStateService.onTrainingObjectStateChanged(new TrainingObjectStateChangedInternalEvent(trainingObject, state.data, this.context));
                                this.invokePredefinedTrainingObjectCallbacks(trainingObject.id, state.data.name);
                            }
                        }
                        // reset the position of the training object
                    }
                    else if ("positionObjectName" in objectCondition) {
                        const positionModelUID = (_a = modelPool.findModelInHierarchy(objectCondition.positionObjectName, roomService.currentRoomModelUID)) === null || _a === void 0 ? void 0 : _a.getUID();
                        if (positionModelUID !== undefined) {
                            const positionModel = modelPool.getModel(positionModelUID);
                            const trainingObjectModel = trainingObject !== undefined ? modelPool.getModel(trainingObject.modelUid) : undefined;
                            if (positionModel !== undefined && trainingObjectModel !== undefined && trainingObject !== undefined) {
                                trainingObjectModel.removeFacet(LinkToModelFacet);
                                const localTransform = computeLocalMatrixFromWorldMatrix(trainingObjectModel, positionModel.getWorldTransform());
                                trainingObjectModel.setTransform(localTransform);
                                explicitlyPositionedObjects.add(objectCondition.objectName);
                                trainingObject.position = trainingObjectModel.getWorldPosition();
                            }
                        }
                        // reset the parent and local transform of the training object
                    }
                    else if ("parentObjectName" in objectCondition) {
                        const parentTrainingObject = this.context.trainingObjectMap.get(objectCondition.parentObjectName);
                        const childModel = trainingObject !== undefined ? modelPool.getModel(trainingObject.modelUid) : undefined;
                        const parentModel = parentTrainingObject !== undefined ? modelPool.getModel(parentTrainingObject.modelUid) : undefined;
                        if (trainingObject === undefined || childModel === undefined || parentTrainingObject === undefined || parentModel === undefined)
                            continue;
                        const offset = explicitlyPositionedObjects.has(objectCondition.objectName)
                            ? worldToLocalTransform(childModel.getWorldTransform(), parentModel.getWorldTransform(), TMP_ATTACHED_LOCAL_TRANSFORM)
                            : this.trainingSceneResetService.getInitialAttachmentOffset(childModel.getUID(), parentModel.getUID());
                        childModel.removeFacet(LinkToModelFacet);
                        childModel.setFacet(new AttachToModelFacet(parentModel.getUID(), {
                            offsetMatrix: convertToNumber16(offset)
                        }));
                    }
                }
            }
            this.context.trainingObjectMap.forEach(trainingObject => {
                const model = modelPool.getModel(trainingObject.modelUid);
                if (model !== undefined)
                    trainingObject.position = model.getWorldPosition();
            });
        });
    }
    /// Training object helper methods ///
    addTrainingObject(trainingObject, force = false) {
        var _a;
        if (this.isActive || force) {
            (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.objects.push(trainingObject);
            this.context.trainingObjectMap.set(trainingObject.id, trainingObject);
        }
        else {
            this.pendingTrainingObjects.add(trainingObject);
        }
    }
    removeTrainingObject(trainingObject) {
        var _a, _b, _c;
        this.pendingTrainingObjects.delete(trainingObject);
        const idx = (_b = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.objects.indexOf(trainingObject)) !== null && _b !== void 0 ? _b : -1;
        if (idx !== -1)
            (_c = this.scenario) === null || _c === void 0 ? void 0 : _c.objects.splice(idx, 1);
        this.context.trainingObjectMap.delete(trainingObject.id);
    }
    updateTrainingObjects(event) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.scenario === undefined)
                return;
            const modelChangedEvents = [];
            const context = event.context;
            // We just add the training event to the context for the duration of the evaluation before we remove it again
            context.trainingEventMetaInformation.set(event.id, new Map());
            for (const trainingObject of this.scenario.objects) {
                const stateChange = trainingObject.update(context);
                if (stateChange === undefined || (stateChange === null || stateChange === void 0 ? void 0 : stateChange.nextState) === undefined)
                    continue;
                modelChangedEvents.push({ trainingObject, nextState: stateChange.nextState });
                console.log(`[training] UpdateTrainingObject object="${String(trainingObject.modelName)}" from="${stateChange.previousState}" to="${stateChange.nextState.name}"`);
            }
            context.trainingEventMetaInformation.delete(event.id);
            // Forward model changed events
            for (const modelChangedEvent of modelChangedEvents) {
                yield this.handleOnEventTriggered(new TrainingObjectStateChangedInternalEvent(modelChangedEvent.trainingObject, modelChangedEvent.nextState, context));
            }
        });
    }
    findTrainingObjectFromContextByUID(uid) {
        const trainingObjects = Array.from(this.context.trainingObjectMap.values());
        for (const trainingObject of trainingObjects) {
            if (trainingObject.modelUid === uid)
                return trainingObject;
        }
        return undefined;
    }
    /// Training stage helper methods ///
    updateStage(event) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (this.scenario === undefined || this.skipStageChangesWhileApplyingPreconditionState)
                return;
            const currentStage = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.getCurrentState();
            if (this.isStageFinal(currentStage)) {
                console.warn("[training] Already in final stage, cannot switch to next stage");
                return;
            }
            // We just add the training event to the context for the duration of the evaluation before we remove it again
            if (event !== undefined)
                event.context.currentEvent = event;
            // Update the stage with the occurred new event
            const stageChange = this.scenario.update(event === null || event === void 0 ? void 0 : event.context);
            if (trainingUserMistakeService.isRevisitErrorMode) {
                const nextStageId = (_b = stageChange === null || stageChange === void 0 ? void 0 : stageChange.nextState) === null || _b === void 0 ? void 0 : _b.id;
                if (nextStageId !== undefined) {
                    if (trainingUserMistakeService.updateStageWithUserMistake(stageChange === null || stageChange === void 0 ? void 0 : stageChange.nextState)) {
                        const currentSequence = trainingUserMistakeService.getCurrentTrainingSequence();
                        if (currentSequence !== undefined) {
                            yield this.processPreconditionStateForSequence(currentSequence);
                        }
                        else {
                            const finalStage = this.scenario.stages.find((value) => value.isFinal);
                            if (finalStage !== undefined) {
                                yield this.switchToNextStage(this.scenario.name, { previousState: stageChange === null || stageChange === void 0 ? void 0 : stageChange.previousState, nextState: finalStage });
                            }
                        }
                        return;
                    }
                }
            }
            // Remove the event from the context again
            if (event !== undefined)
                event.context.currentEvent = undefined;
            if (stageChange === undefined || this.isStageChangeInvalid(stageChange))
                return;
            yield this.switchToNextStage(this.scenario.name, stageChange);
        });
    }
    switchToNextStage(scenarioName, stageChange) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
            const isFinalStage = this.isStageFinal(stageChange.nextState);
            const isErrorStage = ((_a = stageChange.nextState) === null || _a === void 0 ? void 0 : _a.isError) === true;
            const previousStageName = (_c = (_b = stageChange.previousState) === null || _b === void 0 ? void 0 : _b.label) !== null && _c !== void 0 ? _c : "undefined";
            const previousStageId = (_e = (_d = stageChange.previousState) === null || _d === void 0 ? void 0 : _d.id) !== null && _e !== void 0 ? _e : "undefined";
            const nextStageName = (_g = (_f = stageChange.nextState) === null || _f === void 0 ? void 0 : _f.label) !== null && _g !== void 0 ? _g : (isFinalStage ? "FINAL" : "undefined");
            const nextStageId = (_j = (_h = stageChange.nextState) === null || _h === void 0 ? void 0 : _h.id) !== null && _j !== void 0 ? _j : (isFinalStage ? "FINAL" : "undefined");
            console.log(`Stage changed from "${previousStageName}" to "${nextStageName}"`);
            // If we reached an error stage, we add the previous stage to the error service to know where the mistake happened
            if (isErrorStage && this.scenario !== undefined) {
                trainingUserMistakeService.addStage(stageChange);
            }
            this.connection.send(new TrainingStageChangedMessage(previousStageId, nextStageId));
            this.trainingMovementTrackerService.setActiveStage(nextStageId);
            // Process settings for the new stage
            this.processScenarioSettingsBySettingsId(nextStageId);
            if (isFinalStage) {
                this.trainingMovementTrackerService.stop();
                this.unregisterMovementTrackerControllerDelegates();
                // Collect results before leaving XR, as leaving XR disconnects the controllers
                const trainingResults = this.trainingMovementTrackerService.getAllStats();
                if (xrService.isPresenting()) {
                    yield this.connection.askByMessageType(new EnableWebXRMessage(false), AskOkMessage);
                }
                trainingResults.forEach(stats => {
                    stats.violations.forEach(violation => {
                        var _a;
                        const stage = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.stages.find(candidate => candidate.id === violation.stage);
                        if (stage !== undefined)
                            trainingUserMistakeService.addStageViolation(stage);
                    });
                });
                this.logMovementTrackerHistory();
                void MessageService.showEndTraining(scenarioName, trainingResults, () => {
                    this.finishScenario();
                }, () => {
                    this.restartScenario();
                }, () => {
                    void this.revisitStagesWithMadeMistakes();
                });
            }
            else {
                // Only log if we haven't reached the final stage
                this.displayCurrentObjective();
                // Inform others that stage has changed
                yield this.forwardStateChangeEvent(previousStageId, (_l = (_k = stageChange.nextState) === null || _k === void 0 ? void 0 : _k.id) !== null && _l !== void 0 ? _l : "");
            }
        });
    }
    forwardStateChangeEvent(oldStageId, newStageId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.context.stage = newStageId;
            trainingDebugService.setCurrentStageId(newStageId);
            yield this.updatePredefinedTrainingObjectState("STAGE", newStageId);
            yield this.handleOnEventTriggered(new StageChangedEvent(`StageChanged-${newStageId}`, this.context));
            this.connection.send(new TrainingStageChangedMessage(oldStageId, newStageId));
            // Also forward this event to the training user service
            this.trainingUserEventService.updateStage();
        });
    }
    isStageChangeInvalid(stageChange) {
        return stageChange.nextState === undefined && !this.isStageFinal(stageChange.previousState);
    }
    isStageFinal(stage) {
        return (stage === null || stage === void 0 ? void 0 : stage.isFinal) === true;
    }
    displayCurrentObjective() {
        var _a;
        const currentState = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.getCurrentState();
        const message = currentState === null || currentState === void 0 ? void 0 : currentState.label;
        if (message !== undefined && message !== "") {
            this.trainingVrHudService.updateDisplayedTask(message);
            if (!this.trainingVrHudService.getVisibility())
                MessageService.showMessage("info", `Aktuelle Aufgabe: ${message}`, Number.POSITIVE_INFINITY, undefined, "currentObjective");
        }
        historyEventService.addLocalHistoryEvent({
            value: currentState === null || currentState === void 0 ? void 0 : currentState.label,
            type: HistoryEventType.chatMessage,
            clientId: "Stage" // ToDo: Add new field customName that is used instead of the client id in the Chat window
        });
    }
    /// Tracking helper methods ///
    registerMovementTrackerObjects() {
        // register head object for movement tracking
        this.trainingMovementTrackerService.addObject("Kopf", new TrackedCamera("head", camera.perspectiveCam.id), false);
        // Controllers may connect at any time (e.g. when entering VR after the training was started), so we always
        // listen for them instead of only when VR support was already detected at training start.
        xrControllerService.getControllers().forEach(this.handleTrackedControllerConnected);
        this.unregisterMovementTrackerControllerDelegates();
        xrControllerService.onConnectDelegate.add(this.handleTrackedControllerConnected);
        xrControllerService.onDisconnectDelegate.add(this.handleTrackedControllerDisconnected);
        // start movement tracker
        this.trainingMovementTrackerService.start(); // ToDO: Hack used for GMP Conference, fix afterwards!!!
    }
    unregisterMovementTrackerControllerDelegates() {
        xrControllerService.onConnectDelegate.remove(this.handleTrackedControllerConnected);
        xrControllerService.onDisconnectDelegate.remove(this.handleTrackedControllerDisconnected);
    }
    static translateControllerName(hand) {
        if (hand === ControllerHand.RightHand)
            return "rechte Hand";
        if (hand === ControllerHand.LeftHand)
            return "linke Hand";
        return "unbekannter Controller";
    }
    /// Spectator helper methods ///
    enableSpectatorMode() {
        this.connection.send(new DeactivateModuleMessage("CollisionModule"));
    }
    /// Server helper methods ///
    informServerToResetSessionWhenAllClientsDisconnected() {
        const sessionName = localStorage.getItem("session");
        if (sessionName === null) {
            console.warn("Session name is null");
            return;
        }
        this.connection.send(new UpdateSessionResetPolicyMessage(sessionName, SessionResetPolicy.ALL_CLIENTS_DISCONNECTED));
    }
    resetSessionOnServer() {
        const sessionName = localStorage.getItem("session");
        if (sessionName === null) {
            console.warn("Session name is null");
            return;
        }
        this.connection.send(new ResetSessionMessage(sessionName));
    }
    setInputObjectState(objectId, newStateId) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const trainingObject = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.objects.find(obj => obj.id === objectId);
            if (trainingObject === undefined) {
                console.warn(`[training] Input field "${objectId}" has no matching training object`);
                return;
            }
            const nextState = (_b = trainingObject.states) === null || _b === void 0 ? void 0 : _b.find(s => s.name === newStateId);
            if (nextState === undefined) {
                console.warn(`[training] Input field "${objectId}" has no state "${newStateId}"`);
                return;
            }
            const stateNode = trainingObject.getStateByNameOrId(newStateId);
            if (stateNode !== undefined)
                trainingObject.setCurrentState(stateNode);
            yield this.handleOnEventTriggered(new TrainingObjectStateChangedInternalEvent(trainingObject, nextState, this.context));
        });
    }
    /// Update predefined training object states ///
    invokePredefinedTrainingObjectCallbacks(object, newState) {
        var _a;
        (_a = this.scenarioFactory.getPredefinedObjectCallbacks(object)) === null || _a === void 0 ? void 0 : _a.forEach((callback) => {
            callback(newState);
        });
    }
    updatePredefinedTrainingObjectState(objectID, newStateID) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const trainingObject = (_a = this.scenario) === null || _a === void 0 ? void 0 : _a.objects.find((obj) => obj.id === objectID);
            if (trainingObject === undefined)
                throw new Error(`Couldn't find predefined training object ${objectID}`);
            const nextState = (_b = trainingObject.states) === null || _b === void 0 ? void 0 : _b.find((state) => state.name === newStateID);
            if (nextState === undefined)
                throw new Error(`Couldn't find state ${newStateID} of predefined training object ${objectID}`);
            const stateNode = trainingObject.getStateByNameOrId(newStateID);
            if (stateNode !== undefined) {
                trainingObject.setCurrentState(stateNode);
            }
            yield this.handleOnEventTriggered(new TrainingObjectStateChangedInternalEvent(trainingObject, nextState, this.context));
            console.log(`[training] UpdateTrainingObject object="${String(trainingObject.modelName)}" from="${trainingObject.getCurrentState()}" to="${newStateID}"`);
        });
    }
    /// Debug ///
    logMovementTrackerHistory() {
        const statsContent = this.trainingMovementTrackerService.getSpeedHistoryCSV();
        console.warn(`Speed Tracker Stats:\n${statsContent}`);
    }
}
__decorate([
    ModelEventHandler(ModelEventType.TRANSFORM_UPDATED)
], TrainingOrchestratorModule.prototype, "handleModelTransformEvent", null);
